/**
 * CadViewerSwitch — public CAD viewer entry point (PR-A7).
 *
 * Tiny shell that:
 *   1. Detects the file format (DXF / STEP / STL) from the URL or an
 *      explicit ``fileType`` prop.
 *   2. Guards the 5 MB low-RAM fallback — files over the threshold show
 *      a "scarica per visualizzare" CTA instead of mounting the canvas.
 *      Avoids the WebGL OOM crash V1 hit on phones with 2 GB RAM.
 *   3. Mounts the heavy ``<CadViewerImpl>`` lazily through a Suspense
 *      boundary with a Skeleton fallback that holds the layout while the
 *      Three.js + dxf-viewer chunk downloads.
 *
 * Public surface — keep this thin (props in / props out). Everything
 * Three.js-related lives behind the ``React.lazy`` boundary in
 * ``./lazy.ts`` so the main bundle stays out of the heavy graph.
 *
 * Storybook / SSR-friendly: no router / store / fetch / window access.
 */

import * as React from "react";
import { Download as DownloadIcon } from "lucide-react";
import { cn } from "../lib/cn.js";
import { Skeleton } from "../primitives/skeleton.js";
import { CadViewerImplLazy } from "./lazy.js";
import { detectCadFormat } from "./format.js";
import type { CadFormat } from "./format.js";
import type { CadBlock, CadLayer } from "./types.js";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Hard ceiling at which we refuse to mount the viewer and fall back to
 *  a download CTA. 5 MB is the threshold V1's mobile telemetry settled
 *  on — DXFs above this routinely crashed the WebGL context on 2 GB
 *  phones. Step files (server-tessellated to STL) are well under 5 MB
 *  in practice. */
export const CAD_VIEWER_SIZE_LIMIT_BYTES = 5 * 1024 * 1024;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface CadViewerSwitchProps {
  /** Authenticated proxy URL serving the raw CAD bytes (DXF) or the
   *  pre-tessellated STL (STEP/STL). ``null`` renders the empty state. */
  readonly fileUrl: string | null;
  /** Explicit format override. ``"auto"`` (default) resolves from the
   *  URL extension via ``detectCadFormat``. */
  readonly fileType?: CadFormat | "auto";
  /** Original filename — used by the error message + the download CTA. */
  readonly filename?: string;
  /** File size in bytes. When > 5 MB the canvas is skipped and the
   *  user gets a download CTA instead. Pass ``undefined`` to skip the
   *  guard (preferred for STEP / pre-tessellated assets known to fit). */
  readonly fileSize?: number;
  /** Optional explicit width — when omitted the viewer fills its
   *  parent container (the consumer pre-sizes the slot). */
  readonly width?: number;
  /** Optional explicit height — see ``width``. */
  readonly height?: number;
  /** Extra classes applied to the root container. */
  readonly className?: string;
  /** Called once the parser surfaces the layer list (DXF). */
  readonly onLayersDetected?: (layers: ReadonlyArray<CadLayer>) => void;
  /** Called once the parser surfaces the block-type list (DXF). */
  readonly onBlocksDetected?: (blocks: ReadonlyArray<CadBlock>) => void;
  /** Called when the parser or fetch hits a fatal error. */
  readonly onParseError?: (err: Error) => void;
  /** Called when the user clicks the size-limit download CTA. When
   *  omitted, the CTA renders as a regular ``<a download>``. */
  readonly onDownload?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const CANVAS_ARIA_LABEL = "Visualizzatore CAD 3D";

export const CadViewerSwitch: React.FC<CadViewerSwitchProps> = ({
  fileUrl,
  fileType = "auto",
  filename,
  fileSize,
  width,
  height,
  className,
  onLayersDetected,
  onBlocksDetected,
  onParseError,
  onDownload,
}) => {
  const resolvedFormat: CadFormat | null = React.useMemo(() => {
    if (fileType !== "auto") return fileType;
    // Chain detection: try the URL first (MIME / extension), then fall
    // back to the filename. The ``??`` chain V1 used short-circuited on
    // the first truthy URL — proxy URLs like ``/api/v1/.../proxy`` are
    // truthy but carry no extension, so the filename was never tried
    // and the viewer rendered "formato non riconosciuto" (Jun 8 user
    // feedback on the Tecnico hub). Resolving both keeps the URL hint
    // preferred (it can carry an explicit MIME) without losing the
    // .dxf / .step suffix that the filename always carries.
    return detectCadFormat(fileUrl) ?? detectCadFormat(filename);
  }, [fileType, fileUrl, filename]);

  const inlineStyle: React.CSSProperties = {
    ...(typeof width === "number" ? { width: `${String(width)}px` } : null),
    ...(typeof height === "number" ? { height: `${String(height)}px` } : null),
  };

  // ── Empty state (no URL — viewer not opened yet) ──────────────────────
  if (!fileUrl) {
    return (
      <output
        aria-label={CANVAS_ARIA_LABEL}
        className={cn(
          "flex h-full w-full items-center justify-center bg-cream-deep/30 text-[12px] text-ink-3",
          className,
        )}
        style={inlineStyle}
      >
        Nessun disegno selezionato
      </output>
    );
  }

  // ── Unsupported format fallback ───────────────────────────────────────
  if (!resolvedFormat) {
    return (
      <output
        aria-label={CANVAS_ARIA_LABEL}
        className={cn(
          "flex h-full w-full flex-col items-center justify-center gap-2 bg-cream-deep/30 p-4 text-center text-[12px] text-ink-3",
          className,
        )}
        style={inlineStyle}
      >
        <p>Formato non riconosciuto{filename ? ` (${filename})` : ""}.</p>
        <p className="text-[11px] text-ink-3">Supportati: DXF, STEP, STL.</p>
      </output>
    );
  }

  // ── 5 MB hard limit — low-RAM fallback ───────────────────────────────
  if (typeof fileSize === "number" && fileSize > CAD_VIEWER_SIZE_LIMIT_BYTES) {
    return (
      <div
        aria-label={CANVAS_ARIA_LABEL}
        className={cn(
          "flex h-full w-full flex-col items-center justify-center gap-3 bg-cream-deep/30 p-4 text-center",
          className,
        )}
        style={inlineStyle}
      >
        <p className="text-[12px] text-ink-2">
          File troppo grande, scarica per visualizzare
        </p>
        {onDownload ? (
          <button
            type="button"
            onClick={onDownload}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md border border-hairline bg-cream px-3 py-1.5 text-[11px] text-ink-1",
              "transition-colors hover:bg-cream-deep",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/40",
            )}
            aria-label={`Scarica ${filename ?? "disegno"}`}
          >
            <DownloadIcon size={12} aria-hidden />
            Scarica
          </button>
        ) : (
          <a
            href={fileUrl}
            download={filename ?? true}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md border border-hairline bg-cream px-3 py-1.5 text-[11px] text-ink-1",
              "transition-colors hover:bg-cream-deep",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/40",
            )}
            aria-label={`Scarica ${filename ?? "disegno"}`}
          >
            <DownloadIcon size={12} aria-hidden />
            Scarica
          </a>
        )}
      </div>
    );
  }

  // ── Lazy WebGL boundary ──────────────────────────────────────────────
  return (
    <div
      className={cn("relative h-full w-full overflow-hidden", className)}
      style={inlineStyle}
      data-testid="cad-viewer-shell"
      data-format={resolvedFormat}
    >
      <React.Suspense
        fallback={
          <output
            aria-label={CANVAS_ARIA_LABEL}
            aria-busy
            className="flex h-full w-full items-center justify-center"
          >
            <Skeleton className="h-full w-full" style={{ aspectRatio: "4 / 3" }} />
          </output>
        }
      >
        <CadViewerImplLazy
          fileUrl={fileUrl}
          format={resolvedFormat}
          filename={filename}
          ariaLabel={CANVAS_ARIA_LABEL}
          onLayersDetected={onLayersDetected}
          onBlocksDetected={onBlocksDetected}
          onParseError={onParseError}
          onDownload={onDownload}
        />
      </React.Suspense>
    </div>
  );
};
CadViewerSwitch.displayName = "CadViewerSwitch";
